import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Globe } from 'lucide-react';
import { useLanguage, languages } from '../contexts/LanguageContext';

// SVG Flag components - Only US and TR
const FlagUS = () => (
  <svg viewBox="0 0 36 36" className="w-6 h-6">
    <path fill="#B22334" d="M35.445 7C34.752 5.809 33.477 5 32 5H18v2h17.445zM0 25h36v2H0zm18-8h18v2H18zm0-4h18v2H18zM0 21h36v2H0zm4 10h28c1.477 0 2.752-.809 3.445-2H.555c.693 1.191 1.968 2 3.445 2zM18 9h18v2H18z"/>
    <path fill="#EEE" d="M.068 27.679c.017.093.036.186.059.277.026.101.058.198.092.296.089.259.197.509.333.743L.555 29h34.89l.002-.004c.135-.234.245-.484.334-.743.034-.098.066-.196.092-.296.023-.091.042-.184.059-.277.041-.23.068-.463.068-.68H0c0 .217.027.45.068.679zM35.932 19.321c-.017-.093-.036-.186-.059-.277-.026-.101-.058-.198-.092-.296-.089-.26-.197-.509-.333-.744L35.445 18H.555l-.003.004c-.135.234-.245.484-.334.744-.034.098-.066.196-.092.296-.023.091-.042.184-.059.277-.041.23-.068.463-.068.679h36c0-.216-.027-.449-.067-.679zM18 9h18v2H18zm0 4h18v2H18zM0 21h36v2H0zm0 4h36v2H0zm4 10h28c1.477 0 2.752-.809 3.445-2H.555c.693 1.191 1.968 2 3.445 2z"/>
    <path fill="#3C3B6E" d="M18 5H4C1.791 5 0 6.791 0 9v10h18V5z"/>
    <path fill="#FFF" d="M2.001 7.726l.618.449-.236.725L3 8.452l.618.448-.236-.725L4 7.726h-.764L3 7l-.235.726zm2 2l.618.449-.236.725.617-.448.618.448-.236-.725L6 9.726h-.764L5 9l-.235.726zm4 0l.618.449-.236.725.617-.448.618.448-.236-.725.618-.449h-.764L9 9l-.235.726zm4 0l.618.449-.236.725.617-.448.618.448-.236-.725.618-.449h-.764L13 9l-.235.726zm-8 4l.618.449-.236.725.617-.448.618.448-.236-.725.618-.449h-.764L5 13l-.235.726zm4 0l.618.449-.236.725.617-.448.618.448-.236-.725.618-.449h-.764L9 13l-.235.726zm4 0l.618.449-.236.725.617-.448.618.448-.236-.725.618-.449h-.764L13 13l-.235.726zm-6-6l.618.449-.236.725L7 8.452l.618.448-.236-.725L8 7.726h-.764L7 7l-.235.726zm4 0l.618.449-.236.725.617-.448.618.448-.236-.725.618-.449h-.764L11 7l-.235.726zm4 0l.618.449-.236.725.617-.448.618.448-.236-.725.618-.449h-.764L15 7l-.235.726zm-12 4l.618.449-.236.725.617-.448.618.448-.236-.725.618-.449h-.764L3 11l-.235.726zM6.383 12.9L7 12.452l.618.448-.236-.725.618-.449h-.764L7 11l-.235.726h-.764l.618.449zm3.618-1.174l.618.449-.236.725.617-.448.618.448-.236-.725.618-.449h-.764L11 11l-.235.726zm4 0l.618.449-.236.725.617-.448.618.448-.236-.725.618-.449h-.764L15 11l-.235.726zm-12 4l.618.449-.236.725.617-.448.618.448-.236-.725.618-.449h-.764L3 15l-.235.726zM6.383 16.9L7 16.452l.618.448-.236-.725.618-.449h-.764L7 15l-.235.726h-.764l.618.449zm3.618-1.174l.618.449-.236.725.617-.448.618.448-.236-.725.618-.449h-.764L11 15l-.235.726zm4 0l.618.449-.236.725.617-.448.618.448-.236-.725.618-.449h-.764L15 15l-.235.726z"/>
  </svg>
);

const FlagTR = () => (
  <img 
    src="https://customer-assets.emergentagent.com/job_bilingual-web-2/artifacts/709vh498_turk-bayragi-seeklogo.png" 
    alt="Türkçe" 
    className="w-6 h-6 object-contain"
  />
);

const flagComponents = {
  us: FlagUS,
  tr: FlagTR
};

const LanguageSelector = ({ mobile = false }) => {
  const { language, changeLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const currentLang = languages.find(l => l.code === language) || languages[0];
  const FlagComponent = flagComponents[currentLang.flag];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (code) => {
    changeLanguage(code);
    setIsOpen(false);
  };

  if (mobile) {
    return (
      <div className="py-2">
        <div className="flex items-center gap-2 mb-3 px-2">
          <Globe size={16} style={{ color: '#00F0FF' }} />
          <span className="text-sm font-medium" style={{ color: '#94A3B8' }}>{currentLang.name}</span>
        </div>
        <div className="flex flex-wrap gap-2 px-2">
          {languages.map((lang) => {
            const Flag = flagComponents[lang.flag];
            const isActive = language === lang.code;
            return (
              <button
                key={lang.code}
                onClick={() => handleSelect(lang.code)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg transition-all"
                style={{
                  background: isActive ? 'rgba(0, 240, 255, 0.2)' : 'rgba(30, 41, 59, 0.6)',
                  border: isActive ? '1px solid #00F0FF' : '1px solid rgba(0, 240, 255, 0.2)',
                  boxShadow: isActive ? '0 0 10px rgba(0, 240, 255, 0.3)' : 'none'
                }}
                data-testid={`lang-option-mobile-${lang.code}`}
              >
                <Flag />
                <span className="text-sm" style={{ color: isActive ? '#00F0FF' : '#F8FAFC' }}>
                  {lang.name}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-300"
        style={{
          background: 'rgba(30, 41, 59, 0.6)',
          border: '1px solid rgba(0, 240, 255, 0.3)',
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.borderColor = '#00F0FF';
          e.currentTarget.style.boxShadow = '0 0 15px rgba(0, 240, 255, 0.3)';
        }}
        onMouseOut={(e) => {
          if (!isOpen) {
            e.currentTarget.style.borderColor = 'rgba(0, 240, 255, 0.3)';
            e.currentTarget.style.boxShadow = 'none';
          }
        }}
        data-testid="language-selector"
        aria-label="Select language"
      >
        <FlagComponent />
        <ChevronDown 
          size={16} 
          className={`transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
          style={{ color: '#00F0FF' }} 
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div 
          className="absolute right-0 mt-2 py-2 rounded-xl overflow-hidden z-50 min-w-[160px]"
          style={{
            background: 'rgba(15, 23, 42, 0.95)',
            backdropFilter: 'blur(16px)',
            border: '1px solid rgba(0, 240, 255, 0.3)',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5), 0 0 20px rgba(0, 240, 255, 0.2)'
          }}
        >
          {languages.map((lang) => {
            const Flag = flagComponents[lang.flag];
            const isActive = language === lang.code;
            return (
              <button
                key={lang.code}
                onClick={() => handleSelect(lang.code)}
                className="w-full flex items-center gap-3 px-4 py-3 transition-all duration-200"
                style={{
                  background: isActive ? 'rgba(0, 240, 255, 0.15)' : 'transparent',
                  borderLeft: isActive ? '3px solid #00F0FF' : '3px solid transparent'
                }}
                onMouseOver={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.background = 'rgba(0, 240, 255, 0.1)';
                  }
                }}
                onMouseOut={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.background = 'transparent';
                  }
                }}
                data-testid={`lang-option-${lang.code}`}
              >
                <div className="w-7 h-7 rounded overflow-hidden flex items-center justify-center"
                  style={{
                    boxShadow: isActive ? '0 0 10px rgba(0, 240, 255, 0.5)' : 'none',
                    border: isActive ? '1px solid rgba(0, 240, 255, 0.5)' : '1px solid rgba(255,255,255,0.1)'
                  }}>
                  <Flag />
                </div>
                <span 
                  className="font-medium"
                  style={{ 
                    color: isActive ? '#00F0FF' : '#F8FAFC',
                    textShadow: isActive ? '0 0 10px rgba(0, 240, 255, 0.5)' : 'none'
                  }}
                >
                  {lang.name}
                </span>
                {isActive && (
                  <div 
                    className="ml-auto w-2 h-2 rounded-full"
                    style={{ 
                      background: '#00F0FF',
                      boxShadow: '0 0 10px #00F0FF'
                    }}
                  />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
